import React, {useState, useEffect, useCallback, useRef} from "react";
import useCountriesData from "../../hooks/useContriesData";
import { MAX_BOUNDS, MIN_ZOOM, MAX_ZOOM, DEFAULT_MAP_STATE} from "../../config/config";
import { slugify } from "../../utilities/utils";
import { WebMercatorViewport } from "react-map-gl";
import useComponentSize from "@rehooks/component-size";
import DeckGL, { GeoJsonLayer} from "deck.gl";
import MapControls from "../common/map/MapControls";
import MapToolTip from "../common/mapTooltip/MapToolTip";
import styles from "./map-countries.module.scss";
import ListItem from "../common/list/ListItem";
import { getCountryNameByISO3 } from "../../utilities/converter";
import { getLonAndLat } from "../../utilities/utils";

const MapCountries = ({freeVisaCountries}) => {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  // const [selectedCountry, setSelectedCountry] = useState(null);
  const [allCountriesData, setAllCountriesData] = useState(null);

  const [selectedCountries, setSelectedCountries] = useState([]);
  const [unselectedCountries, setUnselectedCountries] = useState([]);
  const [minZoom, setMinZoom] = useState(MIN_ZOOM);
  const [viewport, setViewport] = useState(DEFAULT_MAP_STATE);
  let mapRef = useRef(null);
  let listRef = useRef(null);
  let { width, height } = useComponentSize(mapRef);
 
  const countriesData = useCountriesData();
  const popUpRef = useRef(null);

  useEffect(() => {
    if (width && height) {
      const newViewport = new WebMercatorViewport({
        width: width,
        height: height,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
        zoom: 0,
        longitude: 0,
        latitude: 0,
      }).fitBounds(MAX_BOUNDS);

      setMinZoom(newViewport.zoom);
      setViewport(newViewport);
    }
  }, [width, height]);

  const handleViewStateChange = useCallback((newState) => {
    if (newState.oldViewState !== null) {
      // clamp the zoom
      newState.viewState.zoom =
        newState.viewState.zoom > MAX_ZOOM ? MAX_ZOOM : newState.viewState.zoom;
      newState.viewState.zoom =
        newState.viewState.zoom < MIN_ZOOM ? MIN_ZOOM : newState.viewState.zoom;
      setViewport((v) =>
        v
          ? new WebMercatorViewport({
              ...v,
              ...newState.viewState,
            })
          : null
      );
    }
  }, []);

  const resetZoom = useCallback(() => {
    const newViewport = new WebMercatorViewport({
      width: width,
      height: height,
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      zoom: 0,
      longitude: 0,
      latitude: 0,
    }).fitBounds(MAX_BOUNDS);

    setViewport(newViewport);
  }, [width, height]);

  const zoomIncrement = useCallback(
    (amt) => {
      const newZoom = Math.max(
        Math.min(Math.max(viewport.zoom + amt, 0.001), MAX_ZOOM),
        minZoom
      );
      handleViewStateChange({
        viewState: {
          zoom: newZoom,
          transitionDuration: 5000,
        },
      });
    },
    [viewport, handleViewStateChange, minZoom]
  );



  useEffect(() => {

    const showVisibleCountries = (includeCountries, features) =>{
      const filteredFeatures = features.filter((feature) =>
        includeCountries.includes(feature.properties.ISO3)
      );
      const visibleCountries = {
        type: "FeatureCollection",
        features: filteredFeatures,
      };
      setSelectedCountries(visibleCountries);
    }

    const hideNotVisibleCountries = (includeCountries, features) =>{
        const unfilteredFeatures = features.filter(
          (feature) => !includeCountries.includes(feature.properties.ISO3)
        );
        const notVisibleCountries = {
          type: "FeatureCollection",
          features: unfilteredFeatures,
        };
        setUnselectedCountries(notVisibleCountries);
    }



    if(countriesData){
       
        let convertedFreeVisaCountries = Object.values(freeVisaCountries);
        let features = countriesData.features; 
      

        let filteredData = [];
        let countriesCode = [];

        for(let i = 0; i < convertedFreeVisaCountries.length; i++){
                  
          let obj = {}
          obj['countryCode'] = convertedFreeVisaCountries[i].code;
          obj['country'] = convertedFreeVisaCountries[i].name;
          //North Pole
          obj['longitude'] = 135.0000;
          obj['latitude'] = 90.0000;
          if(getLonAndLat(convertedFreeVisaCountries[i].code, features)){
            const  geoData = getLonAndLat(convertedFreeVisaCountries[i].code, features);
            obj['longitude'] = geoData.longitude;
            obj['latitude'] = geoData.latitude;
          }
          filteredData.push(obj);
          countriesCode.push(convertedFreeVisaCountries[i].code);

          /*
          * Old approach
          if(getLonAndLat(convertedFreeVisaCountries[i].code, features)){
            let obj = {}
            obj['countryCode'] = convertedFreeVisaCountries[i].code;
            obj['country'] = convertedFreeVisaCountries[i].name;
            obj['longitude'] = getLonAndLat(convertedFreeVisaCountries[i].code, features).longitude;
            obj['latitude'] = getLonAndLat(convertedFreeVisaCountries[i].code, features).latitude;
            filteredData.push(obj);
            countriesCode.push(convertedFreeVisaCountries[i].code);
          }else{
            console.log(convertedFreeVisaCountries[i].code + convertedFreeVisaCountries[i].name + ' is not found');
          }
          */
          // filteredData[i] = {
          //   countryCode: freeVisaCountries[i],
          //   country: countryNameFromList,
          //   longitude: getLonAndLat(freeVisaCountries[i], features).longitude,
          //   latitude: getLonAndLat(freeVisaCountries[i], features).latitude
          // }
        }
        // console.log("Filterdata");
        // console.log(filteredData);

        // const includeFilteredCountries = filteredData.map((d) => d.ISO3);

        const sortedData = filteredData.sort((a, b) => 
            a.country.localeCompare(b.country)
        );
   
        setAllCountriesData(sortedData);
        showVisibleCountries(countriesCode, features);
        hideNotVisibleCountries(countriesCode, features);

        // console.log(filteredData);

    }
  },[countriesData, freeVisaCountries]);

  const getProjectionCoordinates = (coords) => {
    if (viewport) {
      return viewport.project([coords[0], coords[1]]);
    }
    return [];
  };


  const handleHover = (data, fromMap = false) => {
   
    if (fromMap) {
      if(data.object){
        const element = document.getElementById(`list-item-${slugify(getCountryNameByISO3(data.object.properties.ISO3))}`);
        listRef.current.scrollTo(0, element.offsetTop - 30);
      }
      setHoveredCountry(data.object ? getCountryNameByISO3(data.object.properties.ISO3) : null);
      // console.log(data.object.properties);
    }else{
      setHoveredCountry(data !== null ? data.country : null);
    }
  };

  // const handleClick = (data) =>{
  //   // setSelectedCountry(data.country);
  //   // popUpRef.current.scrollIntoView({ behavior: "smooth" });
  //   // handleViewStateChange({
  //   //   viewState: {
  //   //     zoom: 2,
  //   //     longitude: data.longitude,
  //   //     latitude: data.latitude,
  //   //     transitionDuration: 5000,
  //   //   },
  //   // });
  // }



  return (

    <div className="innerContainer">



        <div className="row">
            <div className="col-12">
              
              <div className={styles.listWrapper}>

                  <div className={styles.listContainer} ref={listRef}>
                          {allCountriesData &&
                          allCountriesData.map((d) => {
                              return (
                              <ListItem
                                  id={`list-item-${slugify(d.country)}`}
                                  key={d.country}
                                  style={{
                                  backgroundColor:
                                      hoveredCountry === d.country ? "#C1C5C8" : null,
                                  }}
                                  onMouseOver={() => {
                                  handleHover(d,false);
                                  }}
                                  onMouseOut={() => {
                                  handleHover(null);
                                  }}
                                  // onClick={() => handleClick(d)}
                                  data={d}
                            
                              />
                              );
                          })}                        
                  </div>
              </div>

          </div>
          <div className="col-12" style={{'overflow':'hidden'}}>
            <div className={styles.mapColumn}>
            
                <div className={styles.mapWrapper} ref={mapRef}>
                  <DeckGL
                    viewState={viewport}
                    controller={{scrollZoom:false, doubleClickZoom: false}}
                    onViewStateChange={handleViewStateChange}
                  >
                    <GeoJsonLayer
                      id="map-unselected-countries"
                      data={unselectedCountries ? unselectedCountries : null}
                      getLineColor={[255, 255, 255]}
                      getFillColor={[233, 233, 233]}
                      lineWidthUnits={"pixels"}
                      stroked={true}
                    />
                    <GeoJsonLayer
                      id="map-selected-countries"
                      data={selectedCountries ? selectedCountries : null}
                      getLineColor={[64, 83, 99]}
                      getFillColor={[64, 83, 99]}
                      lineWidthUnits={"pixels"}
                      stroked={true}
                      pickable={true}
                      // onHover={(info) => handleHover(info, true)}
                    />
                  </DeckGL>

                  {allCountriesData &&
                    allCountriesData.map((d) => {
                      return (
                        <React.Fragment key={d.country + "-tooltip"}>
                          {hoveredCountry === d.country && (
                            <MapToolTip 
                              name={d.country} 
                              top={getProjectionCoordinates([d.longitude,d.latitude,])[1]-2}
                              left={getProjectionCoordinates([d.longitude,d.latitude,])[0]}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                    <MapControls
                      zoomIncrement={() => zoomIncrement(0.5)}
                      zoomDecrement={() => zoomIncrement(-0.5)}
                      resetZoom={() => resetZoom()}
                    /> 
                </div>
            
                <div ref={popUpRef} className={styles.positionToScroll}></div>
            </div>
            
          </div>
         
        </div>

    </div>

  );
}

export default MapCountries;